import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../features/products/productsSlice';
import {
  addToCart,
  addToCartAsync,
  fetchCart,
  removeFromCart,
  removeFromCartAsync,
  updateCartItemAsync,
  updateQuantity,
} from '../features/cart/cartSlice';
import Header from './Header';
import Footer from './Footer';
import { BROKEN_IMAGE_PLACEHOLDER } from '../config';
import { formatPrice, getImageUrl, getListedProductStock, getListedProductVariants, getProductMrp, getProp } from '../utils/productHelpers';
import './CategoryProductListPage.css';

const FILTER_PREVIEW_COUNT = 3;
const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'name-asc', label: 'Name: A to Z' },
];

function FilterSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="plp-filter-block">
      <button type="button" className="plp-filter-toggle" onClick={() => setOpen((v) => !v)}>
        <span>{title}</span>
        <span className="plp-filter-chevron" aria-hidden="true">{open ? '▼' : '▶'}</span>
      </button>
      {open ? <div className="plp-filter-body">{children}</div> : null}
    </div>
  );
}

const BrandProductListPage = () => {
  const { brandName: brandNameParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const brandName = useMemo(() => {
    if (!brandNameParam) return '';
    try {
      return decodeURIComponent(brandNameParam).trim();
    } catch {
      return brandNameParam.trim();
    }
  }, [brandNameParam]);

  const products = useSelector((state) => state.products.items);
  const productsStatus = useSelector((state) => state.products.status);
  const productsError = useSelector((state) => state.products.error);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);
  const cartItems = useSelector((state) => state.cart.items);

  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);
  const [sortBy, setSortBy] = useState('recommended');
  const [showAllCategories, setShowAllCategories] = useState(false);

  const isSeller = isLoggedIn && userRole === 'Seller';
  const isAdmin = isLoggedIn && userRole === 'Admin';

  useEffect(() => {
    if (isSeller) {
      navigate('/', { replace: true });
    }
  }, [isSeller, navigate]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchCart());
    }
  }, [isLoggedIn, dispatch]);

  useEffect(() => {
    if (!brandName) return;
    dispatch(fetchProducts({ brandName }));
  }, [dispatch, brandName]);

  const categoryOptions = useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => {
      const id = getProp(p, 'categoryId');
      const name = getProp(p, 'categoryName');
      if (id != null && name) {
        map.set(Number(id), String(name));
      }
    });
    return [...map.entries()]
      .map(([id, name]) => ({ categoryId: id, categoryName: name }))
      .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
  }, [products]);

  const brandImageUrl = useMemo(() => {
    const fromState = location.state?.brandImageUrl;
    if (fromState) return fromState;
    const withBrandImage = (products || []).find((p) => getProp(p, 'brandImageUrl'));
    return withBrandImage ? getImageUrl(getProp(withBrandImage, 'brandImageUrl')) : null;
  }, [location.state, products]);

  const mrpBounds = useMemo(() => {
    const values = (products || [])
      .map((p) => getProductMrp(p))
      .filter((v) => !Number.isNaN(v) && v >= 0);
    if (values.length === 0) return { min: 0, max: 500 };
    const min = Math.floor(Math.min(...values));
    const max = Math.ceil(Math.max(...values));
    return { min, max: Math.max(max, min + 1) };
  }, [products]);

  useEffect(() => {
    setPriceMin(mrpBounds.min);
    setPriceMax(mrpBounds.max);
    setSelectedCategoryIds([]);
  }, [brandName, mrpBounds.min, mrpBounds.max]);

  const filteredProducts = useMemo(() => {
    let list = [...(products || [])];
    if (selectedCategoryIds.length > 0) {
      list = list.filter((p) => selectedCategoryIds.includes(Number(getProp(p, 'categoryId'))));
    }
    const min = priceMin ?? mrpBounds.min;
    const max = priceMax ?? mrpBounds.max;
    list = list.filter((p) => {
      const mrp = getProductMrp(p);
      return mrp >= min && mrp <= max;
    });
    switch (sortBy) {
      case 'price-asc':
        list.sort((a, b) => getProductMrp(a) - getProductMrp(b));
        break;
      case 'price-desc':
        list.sort((a, b) => getProductMrp(b) - getProductMrp(a));
        break;
      case 'name-asc':
        list.sort((a, b) =>
          String(getProp(a, 'productName') || '').localeCompare(
            String(getProp(b, 'productName') || ''),
            undefined,
            { sensitivity: 'base' }
          )
        );
        break;
      default:
        break;
    }
    return list;
  }, [products, selectedCategoryIds, priceMin, priceMax, mrpBounds.min, mrpBounds.max, sortBy]);

  const getCartQuantity = useCallback(
    (productId) => {
      const item = cartItems.find((i) => i.productId === productId);
      return item ? item.quantity : 0;
    },
    [cartItems]
  );

  const handleAddToCart = (product, e) => {
    if (e) e.stopPropagation();
    const productId = getProp(product, 'productId');
    const productName = getProp(product, 'productName');
    const price = getProp(product, 'price');
    const originalPrice = getProp(product, 'originalPrice') ?? null;
    const imageUrl = getImageUrl(getProp(product, 'imageUrl'));
    if (getListedProductVariants(product).length > 0) {
      navigate(`/product/${productId}`);
      return;
    }
    const available = getListedProductStock(product);
    if (getCartQuantity(productId) >= available) {
      window.alert(available > 0 ? `Only ${available} in stock.` : 'This item is out of stock.');
      return;
    }
    if (isLoggedIn) {
      dispatch(addToCartAsync({ productId, quantity: 1 }));
    } else {
      dispatch(addToCart({ productId, productName, price, originalPrice, imageUrl, stock: available }));
    }
  };

  const handleIncrement = (product, e) => {
    if (e) e.stopPropagation();
    const productId = getProp(product, 'productId');
    if (getListedProductVariants(product).length > 0) {
      navigate(`/product/${productId}`);
      return;
    }
    const qty = getCartQuantity(productId);
    const available = getListedProductStock(product);
    if (qty >= available) {
      window.alert(available > 0 ? `Only ${available} in stock.` : 'This item is out of stock.');
      return;
    }
    if (isLoggedIn) {
      dispatch(updateCartItemAsync({ productId, quantity: qty + 1 }));
    } else {
      dispatch(updateQuantity({ productId, quantity: qty + 1, stock: available }));
    }
  };

  const handleRemoveFromCart = (product, e) => {
    if (e) e.stopPropagation();
    const productId = getProp(product, 'productId');
    if (isLoggedIn) {
      dispatch(removeFromCartAsync(productId));
    } else {
      dispatch(removeFromCart(productId));
    }
  };

  const handleProductClick = (product) => {
    navigate(`/product/${getProp(product, 'productId')}`);
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const clearFilters = () => {
    setSelectedCategoryIds([]);
    setPriceMin(mrpBounds.min);
    setPriceMax(mrpBounds.max);
  };

  const visibleCategories = showAllCategories
    ? categoryOptions
    : categoryOptions.slice(0, FILTER_PREVIEW_COUNT);

  if (isSeller) return null;

  if (!brandName) {
    return (
      <div className="plp-page">
        <Header />
        <main className="plp-main">
          <p className="plp-status">Invalid brand.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="plp-page">
      <Header />
      <main className="plp-main">
        <nav className="plp-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="plp-breadcrumb-sep">/</span>
          <span>{brandName}</span>
        </nav>

        <div className="plp-layout">
          <aside className="plp-sidebar" aria-label="Filters">
            <div className="plp-filters-head">
              <h2>Filters</h2>
              <button type="button" className="plp-clear-btn" onClick={clearFilters}>
                Clear All
              </button>
            </div>

            {categoryOptions.length > 0 && (
              <FilterSection title="Category">
                <div className="plp-check-list">
                  {visibleCategories.map((cat) => (
                    <label key={cat.categoryId} className="plp-check-label">
                      <input
                        type="checkbox"
                        checked={selectedCategoryIds.includes(cat.categoryId)}
                        onChange={() => toggleCategory(cat.categoryId)}
                      />
                      <span>{cat.categoryName}</span>
                    </label>
                  ))}
                </div>
                {categoryOptions.length > FILTER_PREVIEW_COUNT && (
                  <button
                    type="button"
                    className="plp-more-link"
                    onClick={() => setShowAllCategories((v) => !v)}
                  >
                    {showAllCategories
                      ? 'Show less'
                      : `+ ${categoryOptions.length - FILTER_PREVIEW_COUNT} More`}
                  </button>
                )}
              </FilterSection>
            )}

            <FilterSection title="Price Range By M.R.P.">
              <div className="plp-price-range">
                <input
                  type="range"
                  min={mrpBounds.min}
                  max={mrpBounds.max}
                  value={priceMin ?? mrpBounds.min}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setPriceMin(Math.min(v, priceMax ?? mrpBounds.max));
                  }}
                />
                <input
                  type="range"
                  min={mrpBounds.min}
                  max={mrpBounds.max}
                  value={priceMax ?? mrpBounds.max}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setPriceMax(Math.max(v, priceMin ?? mrpBounds.min));
                  }}
                />
                <div className="plp-price-values">
                  <span>₹{formatPrice(priceMin ?? mrpBounds.min)}</span>
                  <span>₹{formatPrice(priceMax ?? mrpBounds.max)}</span>
                </div>
              </div>
            </FilterSection>
          </aside>

          <div className="plp-content">
            <div className="plp-toolbar">
              <div className="plp-category-head">
                {brandImageUrl ? (
                  <img src={brandImageUrl} alt="" className="plp-category-thumb plp-brand-thumb" />
                ) : (
                  <div className="plp-category-thumb plp-category-thumb-fallback" aria-hidden="true">
                    {String(brandName).charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h1 className="plp-category-title">{brandName}</h1>
                  <p className="plp-item-count">
                    {filteredProducts.length} Item{filteredProducts.length === 1 ? '' : 's'} found
                  </p>
                </div>
              </div>
              <label className="plp-sort-wrap">
                Sort by:
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {productsStatus === 'loading' && (
              <div className="plp-status">Loading products…</div>
            )}
            {productsStatus === 'failed' && (
              <div className="plp-status plp-status-error">
                Error loading products: {productsError}
              </div>
            )}
            {productsStatus === 'succeeded' && filteredProducts.length === 0 && (
              <div className="plp-status">No products found for this brand.</div>
            )}
            {productsStatus === 'succeeded' && filteredProducts.length > 0 && (
              <div className="plp-grid">
                {filteredProducts.map((product) => {
                  const productId = getProp(product, 'productId');
                  const productName = getProp(product, 'productName');
                  const imageUrl = getImageUrl(getProp(product, 'imageUrl'));
                  const displayMrp = getProductMrp(product);
                  const qty = getCartQuantity(productId);

                  return (
                    <article key={productId} className="plp-card">
                      <button
                        type="button"
                        className="plp-card-image-btn"
                        onClick={() => handleProductClick(product)}
                        aria-label={productName}
                      >
                        <img
                          src={imageUrl}
                          alt={productName}
                          className="plp-card-image"
                          onError={(e) => {
                            e.target.src = BROKEN_IMAGE_PLACEHOLDER;
                          }}
                        />
                      </button>
                      <button
                        type="button"
                        className="plp-card-name"
                        onClick={() => handleProductClick(product)}
                      >
                        {productName}
                      </button>
                      <p className="plp-card-mrp">
                        <span className="plp-mrp-label">MRP:</span>
                        <span className="plp-mrp-value"> ₹{formatPrice(displayMrp)}</span>
                      </p>
                      {qty > 0 ? (
                        <div className="quantity-selector plp-qty-selector">
                          <button
                            type="button"
                            className="qty-btn delete-btn"
                            onClick={(e) => handleRemoveFromCart(product, e)}
                            title="Remove from cart"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                          <span className="qty-value">{qty}</span>
                          <button
                            type="button"
                            className="qty-btn add-btn"
                            onClick={(e) => handleIncrement(product, e)}
                            title="Add more"
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="plp-add-cart-btn"
                          onClick={(e) => handleAddToCart(product, e)}
                          disabled={isAdmin}
                        >
                          <span>Add to cart</span>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
                            <circle cx="9" cy="21" r="1" />
                            <circle cx="20" cy="21" r="1" />
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                          </svg>
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BrandProductListPage;
